import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getAiHubStatusChipSx } from "../aiHubChipStyles";
import AssessmentStatusChip from "./AssessmentStatusChip";
import { RELEVANCE_TONES } from "./ObjectAnalysisPairTable";
import {
  formatObjectEntityDetails,
  type ObjectAnalysisGroup,
} from "./objectAnalysisDetailData";

type ObjectAnalysisGroupCardProps = Readonly<{
  group: ObjectAnalysisGroup;
  targetLabel: string;
  expanded: boolean;
  onToggle: () => void;
}>;

const headerCellSx = {
  bgcolor: "#F9FAFB",
  borderBottom: "1px solid #E7ECFC",
  height: 44,
  px: 3,
  py: 1.5,
  fontSize: 14,
  lineHeight: "20px",
  fontWeight: 600,
  color: "#262626",
} as const;

const cellSx = {
  borderBottom: "1px solid #F2F2F2",
  height: 72,
  px: 3,
  py: 1,
  fontSize: 14,
  lineHeight: "20px",
  color: "#595959",
  verticalAlign: "middle",
} as const;

export default function ObjectAnalysisGroupCard({
  group,
  targetLabel,
  expanded,
  onToggle,
}: ObjectAnalysisGroupCardProps) {
  const { source } = group;
  const regionId = `object-analysis-group-${group.key}`;

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: 2,
          py: "14px",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 16,
              lineHeight: "28px",
              fontWeight: 600,
              color: "#000",
            }}
          >
            {source.name}
          </Typography>
          <Typography
            sx={{
              mt: "2px",
              fontSize: 14,
              lineHeight: "20px",
              color: "#737373",
            }}
          >
            {formatObjectEntityDetails(source.code, source.version)}
          </Typography>
        </Box>
        <IconButton
          aria-label={expanded ? `Collapse ${source.name}` : `Expand ${source.name}`}
          aria-expanded={expanded}
          aria-controls={regionId}
          size="small"
          onClick={onToggle}
          sx={{
            transform: expanded ? "rotate(180deg)" : "none",
            transition: "transform 150ms ease",
          }}
        >
          <ExpandMoreIcon sx={{ color: "#6b7280" }} />
        </IconButton>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <TableContainer id={regionId} sx={{ px: 2, pb: 2 }}>
          <Table aria-label={`${source.name} matches`}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerCellSx, width: 90 }}>Rank</TableCell>
                <TableCell sx={{ ...headerCellSx, width: 300 }}>
                  {targetLabel}
                </TableCell>
                <TableCell sx={{ ...headerCellSx, width: 130 }}>
                  Status
                </TableCell>
                <TableCell sx={headerCellSx}>Matching tags</TableCell>
                <TableCell sx={{ ...headerCellSx, width: 214 }}>
                  Relevance Score
                </TableCell>
                <TableCell sx={{ ...headerCellSx, width: 214 }}>
                  Relevance
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {group.rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell sx={cellSx}>{row.rank}</TableCell>
                  <TableCell sx={cellSx}>
                    <Typography
                      sx={{ fontSize: 14, lineHeight: "20px", color: "#262626" }}
                    >
                      {row.target.name}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 12, lineHeight: "20px", color: "#8C8C8C" }}
                    >
                      {formatObjectEntityDetails(
                        row.target.code,
                        row.target.version,
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {row.statusTone === "failed" ? (
                      <AssessmentStatusChip
                        label={row.status}
                        tone="red"
                        showInfoIcon
                        hint={row.errorMessage}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label={row.status}
                        sx={getAiHubStatusChipSx("green")}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {row.matchingTags.length === 0 ? (
                      <Typography sx={{ fontSize: 14, color: "#8c8c8c" }}>
                        -
                      </Typography>
                    ) : (
                      <Box
                        sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}
                      >
                        {row.matchingTags.map((tag) => (
                          <Chip
                            key={tag}
                            size="small"
                            label={tag}
                            sx={getAiHubStatusChipSx("blue")}
                          />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={cellSx}>{row.relevanceScore}</TableCell>
                  <TableCell sx={cellSx}>
                    {row.relevance ? (
                      <Chip
                        size="small"
                        label={row.relevance}
                        sx={getAiHubStatusChipSx(RELEVANCE_TONES[row.relevance])}
                      />
                    ) : (
                      <Typography sx={{ fontSize: 14, color: "#8c8c8c" }}>
                        -
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Box>
  );
}
